import React, { useMemo } from 'react';
import {
  Autocomplete, TextField, Box, Avatar, Typography, InputAdornment,
  Paper, useTheme, alpha
} from '@mui/material';
import { PersonOutline, Search, CheckCircle } from '@mui/icons-material';

export interface TeacherOption {
  _id: string;
  name: string;
  roleName?: string;
}

interface Props {
  teachers: TeacherOption[];
  value: { _id: string; name: string } | null;
  onChange: (teacher: TeacherOption | null) => void;
  /** Lifted disabled flag — used while the row is saving or the page is read-only. */
  disabled?: boolean;
  /** Auto-sizes nicely inside dense table cells. */
  size?: 'small' | 'medium';
  /** Placeholder text shown when nothing is selected. */
  placeholder?: string;
  /** Optional className for the surrounding form-control element. */
  className?: string;
  /** Visual hint that this row has unsaved local edits — adds a soft amber border. */
  pending?: boolean;
}

// Stable initial-color picker so avatars don't flicker on rerender.
const PALETTE = [
  '#3F51B5', '#1976D2', '#0288D1', '#0097A7', '#00897B', '#43A047',
  '#7CB342', '#EF6C00', '#E53935', '#C2185B', '#8E24AA', '#5E35B1'
];
const colorFor = (id: string) => {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) & 0xffffffff;
  return PALETTE[Math.abs(h) % PALETTE.length];
};

/**
 * Compact, premium-feeling teacher picker. Used inside table cells, so it
 * leans on a tight border, a clean avatar on the trigger and inside the
 * popup, and a visible search prompt up top.
 */
const TeacherPickerSelect: React.FC<Props> = ({
  teachers, value, onChange, disabled, size = 'small',
  placeholder = 'Unassigned', className, pending = false
}) => {
  const theme = useTheme();

  const selected = useMemo<TeacherOption | null>(() => {
    if (!value) return null;
    return teachers.find(t => t._id === value._id) ?? { _id: value._id, name: value.name };
  }, [value, teachers]);

  return (
    <Autocomplete<TeacherOption, false, false, false>
      className={className}
      options={teachers}
      value={selected}
      onChange={(_, v) => onChange(v ?? null)}
      getOptionLabel={(opt) => opt.name}
      isOptionEqualToValue={(opt, v) => opt._id === v._id}
      disabled={disabled}
      size={size}
      disableClearable={false}
      autoHighlight
      clearOnEscape
      openOnFocus
      sx={{ minWidth: 200 }}
      slotProps={{
        popper: { sx: { zIndex: theme.zIndex.modal + 1 } },
        listbox: {
          sx: {
            py: 0.5,
            maxHeight: 320,
            '& .MuiAutocomplete-option': {
              borderRadius: 1,
              mx: 0.5,
              my: 0.125,
              minHeight: 'auto',
              transition: 'background-color 100ms ease'
            },
            '& .MuiAutocomplete-option[aria-selected="true"]': {
              bgcolor: alpha(theme.palette.primary.main, 0.08)
            },
            '& .MuiAutocomplete-option.Mui-focused': {
              bgcolor: alpha(theme.palette.primary.main, 0.06)
            }
          }
        }
      }}
      filterOptions={(opts, state) => {
        const q = state.inputValue.trim().toLowerCase();
        if (!q) return opts;
        return opts.filter(o =>
          o.name.toLowerCase().includes(q) ||
          (o.roleName?.toLowerCase().includes(q) ?? false)
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={selected ? '' : placeholder}
          variant="outlined"
          slotProps={{
            input: {
              ...params.InputProps,
              startAdornment: selected ? (
                <InputAdornment position="start" sx={{ ml: 0.25, mr: -0.25 }}>
                  <Avatar
                    sx={{
                      width: 22,
                      height: 22,
                      fontSize: 11,
                      fontWeight: 700,
                      bgcolor: colorFor(selected._id),
                      color: '#fff'
                    }}
                  >
                    {selected.name?.[0]?.toUpperCase() ?? '?'}
                  </Avatar>
                </InputAdornment>
              ) : (
                <InputAdornment position="start" sx={{ ml: 0.25, mr: -0.25 }}>
                  <PersonOutline sx={{ fontSize: 18, color: 'text.disabled' }} />
                </InputAdornment>
              )
            }
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 1.5,
              bgcolor: 'background.paper',
              transition: 'border-color 120ms ease, box-shadow 120ms ease',
              ...(pending && {
                '& fieldset': {
                  borderColor: theme.palette.warning.main,
                  borderStyle: 'dashed'
                },
                '&:hover fieldset': {
                  borderColor: theme.palette.warning.dark
                },
                bgcolor: alpha(theme.palette.warning.main, 0.04)
              }),
              ...(!pending && selected && {
                '& fieldset': {
                  borderColor: alpha(theme.palette.primary.main, 0.35)
                }
              })
            },
            '& .MuiOutlinedInput-input': {
              fontWeight: selected ? 500 : 400,
              color: selected ? 'text.primary' : 'text.disabled'
            }
          }}
        />
      )}
      renderOption={(props, opt) => {
        const { key, ...rest } = props as any;
        const isSelected = selected?._id === opt._id;
        return (
          <Box
            component="li"
            key={key}
            {...rest}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
              py: 0.875,
              px: 1.25
            }}
          >
            <Avatar
              sx={{
                width: 28,
                height: 28,
                fontSize: 12,
                fontWeight: 700,
                bgcolor: colorFor(opt._id),
                color: '#fff',
                flexShrink: 0
              }}
            >
              {opt.name?.[0]?.toUpperCase() ?? '?'}
            </Avatar>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                variant="body2"
                fontWeight={isSelected ? 600 : 500}
                noWrap
                sx={{ color: 'text.primary' }}
              >
                {opt.name}
              </Typography>
              {opt.roleName && (
                <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', lineHeight: 1.2 }}>
                  {opt.roleName}
                </Typography>
              )}
            </Box>
            {isSelected && (
              <CheckCircle sx={{ fontSize: 18, color: 'primary.main', flexShrink: 0 }} />
            )}
          </Box>
        );
      }}
      renderTags={() => null}
      PaperComponent={({ children, ...paperProps }) => (
        <Paper
          {...paperProps}
          elevation={8}
          sx={{
            mt: 0.5,
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            overflow: 'hidden'
          }}
        >
          <Box
            sx={{
              px: 1.25,
              py: 1,
              borderBottom: `1px solid ${theme.palette.divider}`,
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              bgcolor: alpha(theme.palette.text.primary, 0.015)
            }}
          >
            <Search sx={{ fontSize: 16, color: 'text.disabled' }} />
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
              {teachers.length} teacher{teachers.length === 1 ? '' : 's'} · type to filter
            </Typography>
          </Box>
          {children}
        </Paper>
      )}
      noOptionsText={(
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.disabled', py: 1 }}>
          <PersonOutline fontSize="small" />
          <Typography variant="caption">No matching teachers</Typography>
        </Box>
      )}
    />
  );
};

export default TeacherPickerSelect;
